import { Component, OnInit, OnDestroy, ViewChild, HostListener, ViewChildren, QueryList, ChangeDetectionStrategy } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { Enums, IEnums } from '../_shared/enums/enums';
import * as Models from '../_shared/models/models-index';
import { IAuthenticationInfo, User, IOrgLookup, IDealerLookup, IRouteConfig, IRouteDetails } from '../_shared/models/models-index';
import { FilterService, LocalCacheService, ConfigurationService, LocaleService, RoutingConfigService } from '../_shared/services/services-index';
import * as Modals from '../_shared/modals/modals-index';
import { Subscription } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { States } from '../_store/app.reducer';
import { selectLoggedInUser, isLoggedIn, selectOrgLookups } from '../_store/app.selectors';
import { map, tap, take } from 'rxjs/operators';
import { AppSelectors } from '../_store/selector-types';
import { LayoutHelperService } from './layoutHelper.service';
import { rotate90, rotate180 } from '../_shared/utilities/app-animations';
import { environment } from '../../environments/environment';

@Component({
    selector: 'layout-sidebar',
    templateUrl: './sidebar.component.html',
    animations: [rotate90, rotate180],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class LayoutSidebarComponent implements OnInit, OnDestroy {
  @ViewChildren('currentMenuTrigger') menuTriggers: QueryList<MatMenuTrigger>;
  // @ViewChild('leadMenuTrigger') leadMenuTrigger: MatMenuTrigger;
  // @ViewChild('managementMenuTrigger') managementMenuTrigger: MatMenuTrigger;
  // @ViewChild('websiteMenuTrigger') websiteMenuTrigger: MatMenuTrigger;
  // @ViewChild('salesMenuTrigger') salesMenuTrigger: MatMenuTrigger;
  // @ViewChild('dealerResourceMenuTrigger') dealerResourceMenuTrigger: MatMenuTrigger;
  locale: string;
  subscriptions: Subscription[] = [];
  orgLookups: IOrgLookup[];
  orgLookups$ = this.store$.select(AppSelectors.selectOrgLookups);
  dealerLookups$ = this.store$.select(AppSelectors.selectDealerLookups);
  // dynamic nav routes from routing config service
  routeConfigs: Models.IRouteConfig[] = [];
  // Auth/User info
  authInfo: IAuthenticationInfo;
  authInfo$ = this.store$.pipe(select(selectLoggedInUser));
  isAuthenticated$ = this.store$.pipe(select(isLoggedIn));
  isRegionManager$ = this.authInfo$.pipe(
    map(authInfo => this.configService.role.isOrg2Role(authInfo.role)));

  constructor(
    public configService: ConfigurationService,
    private routingConfigService: RoutingConfigService,
    public dialog: MatDialog,
    private localeService: LocaleService,
    public layoutHelper: LayoutHelperService,
    private router: Router,
    private store$: Store<States.AppState>) {
    // Navigation handled by existing RoutingConfigService
  }

  enums: IEnums = Enums;

  ngOnInit() {
    // Get navigation routes from the existing routing config service
    this.subscriptions.push(
      this.authInfo$.subscribe(authInfo => {
        this.authInfo = authInfo;
        // Update routes when auth info changes
        this.routeConfigs = this.routingConfigService.userNavRouteConfigs;
      })
    );

    this.subscriptions.push(
      this.localeService.locale$.pipe(tap(loc => this.locale = loc)).subscribe(),

      this.store$.select(selectOrgLookups).pipe(
        tap(orgLookups => this.orgLookups = orgLookups)).subscribe(),

      // whenever user clicks on navigation links we should close sidebar
      // and all popups
      this.router.events.subscribe(event => {
        if (event instanceof NavigationStart) {
          this.closeMobileSidebar();
          this.closeAllPopups();
        }
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => {
      try { sub?.unsubscribe(); } catch (err) { }
    });
  }

  goToLink(url: string) {
    window.open(url, '_blank');
  }

  /**
   * Navigate to route (dynamic or static)
   */
  navigate(parent: Models.IRouteConfig, detail: Models.IRouteDetails) {
    let urlTree;

    if (parent.isStatic) {
      // For static routes, navigate using parent and detail route
      urlTree = this.router.createUrlTree([detail.parent || parent.category, detail.route]);
    } else {
      // For dynamic routes, use the parent.category structure
      urlTree = this.router.createUrlTree([parent.category, detail.route]);
    }

    if (detail.routeData?.newTab) {
      window.open(this.router.serializeUrl(urlTree), '_blank');
    } else {
      this.router.navigateByUrl(urlTree);
    }
  }

  openMenu(menuTrigger: MatMenuTrigger) {
    // Voodoo time:
    const selectedMenuId = menuTrigger['_element']['nativeElement']['id'];
    const routeConfig = this.routeConfigs.find(r => r.id === selectedMenuId);
    const showChildren = !!(routeConfig?.children && routeConfig.children.length > 0);

    this.closeAllPopups();
    if (!!showChildren && this.isMobileSidebarOpened()) {
      menuTrigger.openMenu();
    }
  }

  openExternalTool(routeConfig: IRouteDetails, openInNewTab: boolean) {
    const url = this.router.serializeUrl(this.router.createUrlTree([`${routeConfig.parent}/${routeConfig.route}`], { queryParams: { app: routeConfig.routeData.app } }));

    if (!!openInNewTab) {
      window.open(url, '_blank');
    }
    else {
      window.open(url);
    }
  }

  closeAllPopups() {
    this.menuTriggers.forEach(m => m.closeMenu());
  }

  @HostListener('document:click', ['$event.target'])
  onDocumentClick(targetElement: HTMLElement) {
    // close all children sidebar windows whenever we click anywhere
    this.closeAllPopups();
  }

  closeMobileSidebar() {
    document.querySelector('body').classList.remove('sidebar-mobile-show');
  }

  isMobileSidebarOpened(): boolean {
    // if is in desktop mode just return true to pass check above
    if (window.innerWidth >= 991) {
      return true;
    }

    return document.querySelector('body').classList.contains('sidebar-mobile-show')
  }

}
